import styled from "styled-components";

import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../utils/constants";
import { BaseButton } from "../../../../components/Button";

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  border-radius: ${SPACING.md}px;
  background: var(--color-background-2);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
  overflow: hidden;

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  background: var(--color-tab-background);
  border-bottom: 1px solid var(--color-border-light);

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.tablet.cardPadding}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const HeaderTitleBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${SPACING.sm}px;
`;

export const Title = styled.h2`
  font-size: var(--text-lg);
  font-weight: var(--font-weight-bold);
  margin: 0;
  color: var(--color-text-default);
`;

export const HeaderPills = styled.span`
  font-style: italic;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

export const CollapseButton = styled(BaseButton)`
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

export const Body = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.lg}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;

  @media ${QUERIES.laptopAndUp} {
    grid-template-columns: 1.6fr 1fr;
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
`;

export const ChartWrap = styled.div`
  width: 100%;
  min-height: 300px;
`;

export const PanelTitle = styled.h3`
  font-size: var(--text-md);
  font-weight: var(--font-weight-bold);
  margin: 0;
  color: var(--color-text-default);
`;

export const PanelSubtitle = styled.p`
  font-style: italic;
  font-size: var(--text-sm);
  margin: 0;
  color: var(--color-text-secondary);
`;

export const CategoryList = styled.ul`
  list-style: none;
  margin: ${SPACING.xs}px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm + SPACING.xs}px;
`;

// Fixed value column so every progress track shares the same start and end,
// regardless of how wide each formatted amount renders.
const CATEGORY_VALUE_WIDTH = SPACING.xxl + SPACING.xxl + SPACING.md;

export const CategoryRow = styled.li`
  display: grid;
  grid-template-columns: minmax(88px, 0.9fr) 1fr ${CATEGORY_VALUE_WIDTH}px;
  align-items: center;
  gap: ${SPACING.xs}px;
`;

export const LabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const CategorySwatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
  background: ${(props) => props.$color};
`;

export const CategoryLabel = styled.span`
  font-size: var(--text-base);
  color: var(--color-text-default);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CategoryValue = styled.span`
  font-size: var(--text-base);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
  white-space: nowrap;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

export const CategoryEmpty = styled.p`
  margin: ${SPACING.sm}px 0 0;
  font-size: var(--text-base);
  color: var(--color-text-muted);
`;

export const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.xxl}px ${SPACING.lg}px;
  text-align: center;
  color: var(--empty-state-icon-color);
`;

export const EmptyHeadline = styled.p`
  font-weight: var(--font-weight-bold);
  margin: 0;
  color: var(--color-text-default);
`;

export const EmptyHelper = styled.p`
  margin: 0;
  font-size: var(--text-base);
  color: var(--color-text-muted);
`;

export const TooltipBox = styled.div`
  background: var(--color-background-2);
  border: 1px solid var(--color-border-light);
  border-radius: ${SPACING.xs}px;
  box-shadow: var(--shadow-small);
  padding: ${SPACING.sm}px ${SPACING.md}px;
`;

export const TooltipTitle = styled.p`
  margin: 0 0 ${SPACING.xs}px;
  font-size: var(--text-base);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  font-size: var(--text-base);
  color: var(--color-text-default);
`;

export const TooltipLabel = styled.span`
  flex: 1;
  color: var(--color-text-secondary);
`;

export const TooltipValue = styled.span`
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
`;
